import React, { useState, useEffect } from 'react';
import { MapPin, FileDown, Clock, CreditCard, AlertCircle, Lock, LayoutGrid, List } from 'lucide-react';
import { paymentsApi } from '../api/client';
import './MesPoints.css';

const METHOD_LABELS = {
  stripe: 'Carte bancaire',
  mvola:  'MVola',
  orange: 'Orange Money',
  airtel: 'Airtel Money',
};

const METHOD_COLORS = {
  stripe: '#818cf8',
  mvola:  '#e11d48',
  orange: '#f97316',
  airtel: '#dc2626',
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function MesPoints() {
  const [purchases, setPurchases] = useState([]);
  const [details,   setDetails]   = useState({});
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [view,      setView]      = useState('grid'); // 'grid' | 'list'

  useEffect(() => {
    setLoading(true);
    paymentsApi.myPoints()
      .then(r => { setPurchases(r.data); setLoading(false); })
      .catch(() => { setError('Impossible de charger vos points.'); setLoading(false); });
  }, []);

  const fetchDetail = async (pointId) => {
    if (details[pointId]) return;
    try {
      const r = await paymentsApi.pointDetail(pointId);
      setDetails(prev => ({ ...prev, [pointId]: r.data }));
    } catch { /* accès refusé */ }
  };

  useEffect(() => {
    purchases.forEach(p => fetchDetail(p.point));
  }, [purchases]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasData = !loading && purchases.length > 0;

  return (
    <div className="mp-page">
      {/* ── En-tête ── */}
      <div className="mp-header">
        <div className="mp-header-icon">
          <MapPin size={18} />
        </div>
        <div className="mp-header-text">
          <h1 className="mp-title">Mes Points</h1>
          <p className="mp-subtitle">Points géodésiques achetés — coordonnées Laborde déverrouillées</p>
        </div>
        {hasData && (
          <div className="mp-view-toggle">
            <button
              className={`mp-view-btn${view === 'grid' ? ' mp-view-btn--active' : ''}`}
              onClick={() => setView('grid')}
              title="Vue cartes"
            >
              <LayoutGrid size={15} />
            </button>
            <button
              className={`mp-view-btn${view === 'list' ? ' mp-view-btn--active' : ''}`}
              onClick={() => setView('list')}
              title="Vue liste"
            >
              <List size={15} />
            </button>
          </div>
        )}
      </div>

      {/* ── Chargement ── */}
      {loading && (
        <div className="mp-loading">
          <div className="mp-spinner" />
          Chargement…
        </div>
      )}

      {/* ── Erreur ── */}
      {error && (
        <div className="mp-error">
          <AlertCircle size={14} />{error}
        </div>
      )}

      {/* ── Liste vide ── */}
      {!loading && !error && purchases.length === 0 && (
        <div className="mp-empty">
          <div className="mp-empty-icon"><Lock size={32} /></div>
          <div className="mp-empty-text">Vous n'avez pas encore acheté de points géodésiques.</div>
          <div className="mp-empty-sub">Utilisez la carte interactive pour accéder aux coordonnées Laborde d'un point.</div>
        </div>
      )}

      {/* ── Vue cartes ── */}
      {hasData && view === 'grid' && (
        <div className="mp-grid">
          {purchases.map(purchase => {
            const det = details[purchase.point];
            const methodColor = METHOD_COLORS[purchase.payment_method] ?? '#818cf8';
            return (
              <div key={purchase.id} className="mp-card">
                <div className="mp-card-head">
                  <div className="mp-card-name">
                    <div className="mp-card-dot" />
                    {purchase.point_nom || `Point #${purchase.point}`}
                  </div>
                  <span className="mp-badge-bought">ACHETÉ</span>
                </div>
                <div className="mp-card-body">
                  <div className="mp-section-label">WGS 84 (libre)</div>
                  <div className="mp-coords-row">
                    <Coord label="Lat" value={purchase.latitude  != null ? Number(purchase.latitude).toFixed(6)  : '—'} />
                    <Coord label="Lng" value={purchase.longitude != null ? Number(purchase.longitude).toFixed(6) : '—'} />
                  </div>
                  <div className="mp-section-label">LABORDE (déverrouillé)</div>
                  {det ? (
                    <div className="mp-coords-row">
                      <Coord label="X" value={det.laborde_x ?? '—'} accent />
                      <Coord label="Y" value={det.laborde_y ?? '—'} accent />
                    </div>
                  ) : (
                    <div className="mp-loading-coords">
                      <div className="mp-spinner-mini" />Chargement…
                    </div>
                  )}
                  {det?.pdf_url && (
                    <a href={det.pdf_url} target="_blank" rel="noopener noreferrer" className="mp-pdf-link">
                      <FileDown size={13} /> Télécharger la fiche PDF
                    </a>
                  )}
                  <div className="mp-card-footer">
                    <div className="mp-card-method">
                      <CreditCard size={11} style={{ color: methodColor }} />
                      <span style={{ color: methodColor, fontWeight: 600 }}>
                        {METHOD_LABELS[purchase.payment_method] ?? purchase.payment_method}
                      </span>
                      <span>— {purchase.amount_mga} MGA</span>
                    </div>
                    <div className="mp-card-date">
                      <Clock size={10} />{formatDate(purchase.created_at)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Vue liste ── */}
      {hasData && view === 'list' && (
        <div className="mp-table-wrap">
          <table className="mp-table">
            <thead>
              <tr>
                <th>Point</th>
                <th>Latitude</th>
                <th>Longitude</th>
                <th>Laborde X</th>
                <th>Laborde Y</th>
                <th>Méthode</th>
                <th>Montant</th>
                <th>Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {purchases.map(purchase => {
                const det = details[purchase.point];
                const methodColor = METHOD_COLORS[purchase.payment_method] ?? '#818cf8';
                return (
                  <tr key={purchase.id} className="mp-tr">
                    <td className="mp-td mp-td--name">
                      <div className="mp-list-dot" />
                      {purchase.point_nom || `Point #${purchase.point}`}
                    </td>
                    <td className="mp-td mp-td--mono">
                      {purchase.latitude != null ? Number(purchase.latitude).toFixed(6) : '—'}
                    </td>
                    <td className="mp-td mp-td--mono">
                      {purchase.longitude != null ? Number(purchase.longitude).toFixed(6) : '—'}
                    </td>
                    <td className="mp-td mp-td--mono mp-td--accent">
                      {det ? (det.laborde_x ?? '—') : <span className="mp-spinner-mini" />}
                    </td>
                    <td className="mp-td mp-td--mono mp-td--accent">
                      {det ? (det.laborde_y ?? '—') : <span className="mp-spinner-mini" />}
                    </td>
                    <td className="mp-td">
                      <span style={{ color: methodColor, fontWeight: 600, fontSize: 11 }}>
                        {METHOD_LABELS[purchase.payment_method] ?? purchase.payment_method}
                      </span>
                    </td>
                    <td className="mp-td mp-td--mono">{purchase.amount_mga} MGA</td>
                    <td className="mp-td mp-td--date">{formatDate(purchase.created_at)}</td>
                    <td className="mp-td mp-td--action">
                      {det?.pdf_url && (
                        <a href={det.pdf_url} target="_blank" rel="noopener noreferrer" className="mp-list-pdf" title="Télécharger la fiche PDF">
                          <FileDown size={13} />
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Coord({ label, value, accent }) {
  return (
    <div className="mp-coord">
      <div className="mp-coord-label">{label}</div>
      <div className={accent ? 'mp-coord-value mp-coord-value--accent' : 'mp-coord-value'}>{value}</div>
    </div>
  );
}
